import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ArrowUpRight, Lock } from "lucide-react";
import { api, formatUSD } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Portal() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, n] = await Promise.all([
        api.get("/projects"),
        api.get("/notes"),
      ]);
      setProjects(p.data);
      // Investors only see notes marked shared
      setNotes((n.data || []).filter(x => x.visibility === "shared"));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen">
      <section className="bg-[#0B1F3A] text-[#F8F7F2] pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow" style={{ color: "#D4AF37" }}>Investor Portal</div>
              <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">Welcome, {user?.name?.split(" ")[0]}.</h1>
              <div className="text-sm text-[#F8F7F2]/70 mt-2">Role: <span className="uppercase tracking-widest text-[#D4AF37]">{user?.role}</span></div>
            </div>
            {user?.role === "admin" && (
              <Link to="/dashboard" className="border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#0B1F3A]">
                Open Admin Dashboard →
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-6">
          <div>
            <div className="eyebrow">Active Portfolio</div>
            <h2 className="font-display text-3xl mt-2 text-[#0B1F3A]">Live Projects</h2>
          </div>
          {loading && <div className="text-[#5F6B7A]">Loading…</div>}
          {!loading && projects.filter(p => ["Active", "Planning"].includes(p.status)).map((p, i) => (
            <motion.div
              key={p.project_id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-[#0B1F3A]/10 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#3399FF]">{p.sector} · {p.region}</div>
                  <div className="font-display text-2xl mt-2 text-[#0B1F3A]">{p.name}</div>
                </div>
                <div className="text-right">
                  <div className="numeral text-2xl text-[#0B1F3A]">{formatUSD(p.projected_revenue_usd)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#5F6B7A]">Projected Revenue</div>
                </div>
              </div>
              <p className="text-sm text-[#5F6B7A] mt-4">{p.description}</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-[#0B1F3A]/10">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${p.progress_pct}%` }} />
                </div>
                <div className="text-xs text-[#0B1F3A] font-semibold">{p.progress_pct}%</div>
              </div>
            </motion.div>
          ))}
          {!loading && projects.length === 0 && (
            <div className="border border-dashed border-[#0B1F3A]/20 p-10 text-center">
              <div className="text-[#5F6B7A] text-sm">No portfolio items shared with your account yet.</div>
              <div className="text-xs text-[#5F6B7A] mt-2">Contact your relationship manager for a briefing.</div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="eyebrow">Data Room</div>
            <h2 className="font-display text-3xl mt-2 text-[#0B1F3A]">Secure Documents</h2>
          </div>
          {[
            "Q4 Investor Letter — 2025.pdf",
            "Fund I Subscription Documents.pdf",
            "ESG & Governance Policy.pdf",
            "Portfolio Company Roster.pdf",
          ].map((f, i) => (
            <div key={i} className="bg-white border border-[#0B1F3A]/10 p-4 flex items-center gap-4 group hover:border-[#D4AF37] transition cursor-not-allowed opacity-90">
              <FileText size={18} className="text-[#D4AF37]" />
              <div className="flex-1 text-sm text-[#0B1F3A]">{f}</div>
              <Lock size={14} className="text-[#5F6B7A]" />
            </div>
          ))}
          <div className="text-[11px] text-[#5F6B7A] px-1">
            Full document library unlocks after NDA execution — request via your relationship manager.
          </div>

          <div className="bg-[#0B1F3A] text-[#F8F7F2] p-6 mt-8">
            <div className="eyebrow" style={{ color: "#D4AF37" }}>Team Updates</div>
            <div className="mt-4 space-y-4 max-h-[280px] overflow-y-auto thin-scroll">
              {notes.length === 0 && <div className="text-xs text-[#F8F7F2]/60">No shared updates yet.</div>}
              {notes.map(n => (
                <div key={n.note_id} className="border-l-2 border-[#D4AF37] pl-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{n.source}</div>
                  <div className="text-sm text-[#F8F7F2] mt-1">{n.body}</div>
                  <div className="text-[10px] text-[#F8F7F2]/60 mt-1">{n.author_name} · {new Date(n.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
